import React, { useCallback, useEffect, useState } from "react";
import ClawbackReportView from "../../views/clawbackReport";
import { useDispatch, useSelector } from "react-redux";
import config from "../../utils/config";
import {
  commonActionDispatcher,
  triggerDistRetailerFilter,
  triggerFetchFilterList,
  updateFilterValue,
  resetData,
} from "../../redux/actions";
import { toggleDateAlert, hideAlertEd1 } from "../../redux/actions/ed1AndEd2";
import { getPartnerRoleName, mapTokenToRoleId } from "../../utils/common";
import {
  triggerShowHeader,
  setClawbackDashboard,
  unsetClawbackDashboard,
} from "../../redux/actions/action";

const ClawbackReport = () => {
  const {
    filterValue,
    headerSelectedDateFrom,
    headerSelectedDateTo,
    filterList,
    filteredArray,
    getAllAPI: { loading, clawbackPayoutObj },
  } = useSelector((state) => state.homeReducer);
  const dispatch = useDispatch();
  const { showAlert, showDateAlert, showErrorAlert } = useSelector(
    (state) => state.Ed1AndEd2Reducer
  );

  const {
    partnerName,
    orgId,
    agentId,
    site,
    name,
    selectedState,
    selectedRegion,
    selectedRole,
    selectedDistributor,
  } = filterValue;

  const [searchFields, setSearchFields] = useState(null);
  const [zdArdMardList, setZdArdMardList] = useState(null);
  const [retailerList, setRetailerList] = useState({
    loading: false,
    data: null,
    error: null,
  });
  const [distributorList, setDistributorList] = useState({
    loading: false,
    data: null,
    error: null,
  });
  const [rdsList, setRdsList] = useState(null);
  const [clawbackDetails, setClawbackDetails] = useState({
    loading: false,
    data: null,
    error: null,
  });
  const [clawbackSummary, setClawbackSummary] = useState({
    loading: false,
    data: null,
    error: null,
  });
  const [showClawbackSummary, setShowClawbackSummary] = useState(false);
  const [timeStamp, setTimestamp] = useState({
    loading: false,
    data: null,
    error: null,
  });
  const [csvLoading, setCsvLoading] = useState({
    loading: false,
    data: null,
    error: null,
  });
  const [showDownloadIcon, setShowDownloadIcon] = useState(false);
  const [jcValue, setJcValue] = useState("");

  const urlList =
    clawbackPayoutObj?.modSecMasterTxDtos?.[0]?.secTilesMasterTxDtos.reduce(
      (acc, tile) => {
        acc[tile?.tileName?.toLowerCase()] = {
          ...(tile?.isTileActive === "Y" && {
            tileEndPointUrl: tile?.tileEndPointUrl?.replace(/^\/|\/$/g, ""),
          }),
          ...(tile?.isCsvActive === "Y" && {
            csvEndPointUrl: tile?.csvEndPointUrl?.replace(/^\/|\/$/g, ""),
          }),
        };
        return acc;
      },
      {}
    );

  const buildClawbackParams = (
    searchFields,
    headerSelectedDateFrom,
    headerSelectedDateTo,
    orgId,
    agentId
  ) => {
    const getId = () => {
      if ((searchFields?.retailer ?? "ALL") !== "ALL")
        return searchFields.retailer.split("-")[1]?.trim();
      if ((searchFields?.RDS ?? "ALL") !== "ALL")
        return searchFields.RDS.split("-")[2]?.trim();
      if ((searchFields?.distributor ?? "ALL") !== "ALL")
        return searchFields.distributor.split("-")[1]?.trim();
      if ((searchFields?.partner ?? "ALL") !== "ALL")
        return searchFields.partner.split("-").pop()?.trim();
      return orgId ?? agentId;
    };

    const getPartnerIdentity = () => {
      if ((searchFields?.retailer ?? "ALL") !== "ALL") return 3;
      if (
        ((searchFields?.RDS || searchFields?.distributor) ?? "ALL") !== "ALL" ||
        searchFields?.partner?.includes("RDS - ") ||
        partnerName?.toLowerCase()?.includes("regional")
      )
        return 2;
      return 1;
    };

    return {
      fromDate: headerSelectedDateFrom,
      toDate: headerSelectedDateTo,
      id: getId(),
      partnerIdentity: getPartnerIdentity(),
    };
  };

  const fetchSummary = useCallback(
    (searchFields) => {
      setClawbackSummary({ loading: true, data: null, error: null });
      const obj = {
        headers: {
          "Content-Type": "application/json",
          url: `/${urlList?.summary?.tileEndPointUrl}`,
        },
        type: "POST",
        url: `${config.apiBasePath.baseIp}clawback-payout`,
        params: buildClawbackParams(
          searchFields,
          headerSelectedDateFrom,
          headerSelectedDateTo,
          orgId,
          agentId
        ),
        callback: (res) => {
          setShowDownloadIcon(true);
          setClawbackSummary({
            loading: false,
            data: res?.content,
            error: null,
          });
        },
        errCallback: (err) => {
          setClawbackSummary({
            loading: false,
            data: null,
            error: err?.apierror?.message || err?.message,
          });
        },
      };

      dispatch(commonActionDispatcher(obj));
    },
    [
      dispatch,
      urlList,
      headerSelectedDateFrom,
      headerSelectedDateTo,
      orgId,
      agentId,
    ]
  );

  const fetchDetails = useCallback(
    (searchFields) => {
      setClawbackDetails({ loading: true, data: null, error: null });

      const obj = {
        headers: {
          "Content-Type": "application/json",
          url: `/${urlList?.details?.tileEndPointUrl}`,
        },
        type: "POST",
        url: `${config.apiBasePath.baseIp}clawback-payout`,
        params: buildClawbackParams(
          searchFields,
          headerSelectedDateFrom,
          headerSelectedDateTo,
          orgId,
          agentId
        ),
        callback: (res) => {
          setClawbackDetails({
            loading: false,
            data: res?.content,
            error: null,
          });
        },
        errCallback: (err) => {
          setClawbackDetails({
            loading: false,
            data: null,
            error: err?.apierror?.message || err?.message,
          });
        },
      };

      dispatch(commonActionDispatcher(obj));
    },
    [
      dispatch,
      urlList,
      headerSelectedDateFrom,
      headerSelectedDateTo,
      orgId,
      agentId,
    ]
  );

  const toggleShowDetails = useCallback(
    (searchFields) => {
      if (!showClawbackSummary) {
        if (!clawbackDetails?.data && !clawbackDetails?.loading) {
          setShowClawbackSummary(true);
          fetchDetails(searchFields);
        } else {
          setShowClawbackSummary(true);
        }
      } else {
        setShowClawbackSummary(false);
      }
    },
    [showClawbackSummary, clawbackDetails, fetchDetails]
  );

  const fetchTimestamp = useCallback(() => {
    setTimestamp({ loading: true, data: null, error: null });
    const obj = {
      headers: {
        "Content-Type": "application/json",
        url: `/timestamp/get-ftdupdated`,
      },
      type: "POST",
      url: `${config.apiBasePath.baseIp}clawback-timeStamp`,
      params: { tableNameFTD: "CLAWBACK_DETAILS", columnName: "RH_LOAD_MONTH" },
      callback: (res) => {
        setTimestamp({ loading: false, data: res?.content, error: null });
      },
      errCallback: (err) => {
        setTimestamp({ loading: false, data: null, error: err?.message });
      },
    };
    dispatch(commonActionDispatcher(obj));
  }, [dispatch]);

  const triggerDownload = (csvString, filename) => {
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadCSV = useCallback(
    (searchFields) => {
      const dateDiff =
        (new Date(headerSelectedDateTo) - new Date(headerSelectedDateFrom)) /
        (1000 * 60 * 60 * 24);

      if (dateDiff > 30) {
        dispatch(toggleDateAlert());
        setTimeout(() => {
          dispatch(toggleDateAlert());
        }, 5000);
        return;
      }
      setCsvLoading({ loading: true, data: null, error: null });

      const obj = {
        headers: {
          "Content-Type": "application/json",
          url: `/${urlList?.summary?.csvEndPointUrl}`,
        },
        type: "POST",
        url: `${config.apiBasePath.baseIp}clawback-payout`,
        params: buildClawbackParams(
          searchFields,
          headerSelectedDateFrom,
          headerSelectedDateTo,
          orgId,
          agentId
        ),
        callback: (res) => {
          const data = res?.content;
          if (!Array.isArray(data) || data.length === 0) {
            const csv = "No data available\n";
            triggerDownload(csv, "Clawback_Report.csv");
            setCsvLoading({ loading: false, data: [], error: null });
            return;
          }
          const headers = Object.keys(data[0]);
          const csvRows = [
            headers.join(","),
            ...data.map((row) =>
              headers
                .map(
                  (field) =>
                    `"${(row[field] ?? "").toString().replace(/"/g, '""')}"`
                )
                .join(",")
            ),
          ];
          const csvContent = csvRows.join("\n");
          triggerDownload(csvContent, "Clawback_Report.csv");
          setCsvLoading({ loading: false, data, error: null });
        },
        errCallback: (err) => {
          setCsvLoading({ loading: false, data: null, error: err.message });
          const csv = "No data available\n";
          triggerDownload(csv, "error.csv");
        },
      };
      dispatch(commonActionDispatcher(obj));
    },
    [
      dispatch,
      headerSelectedDateFrom,
      headerSelectedDateTo,
      urlList,
      orgId,
      agentId,
    ]
  );

  const onRunClick = () => {
    setShowDownloadIcon(false);
    setClawbackDetails({ loading: false, data: null, error: null });
    setShowClawbackSummary(false);
    const obj = {
      role: mapTokenToRoleId(searchFields?.partner?.split("-")[0]),
      roleType: partnerName?.toUpperCase(),
      partnerRoleType: partnerName,
      forTiles: "NO",
      site: site,
      callback: fetchSummary(searchFields),
    };
    const updatedObj = {
      ...filterValue,
      selectedPartnerRoleName: searchFields?.partner?.split("-")[0]?.trim(),
      selectedPartnerRoleType: mapTokenToRoleId(
        searchFields?.partner?.split("-")[0]
      ),
      selectedRole: searchFields?.partner,
      selectedRegion: searchFields?.region,
      selectedState: searchFields?.state,
      selectedCategory: searchFields?.retailer,
      selectedRchain: searchFields?.RDS,
    };
    dispatch(updateFilterValue(updatedObj));
    dispatch(resetData());
  };

  const distRetailerFilter = useCallback(
    (id, type = "retailer") => {
      type === "distributor"
        ? setDistributorList({ loading: true, data: null, error: null })
        : setRetailerList({ loading: true, data: null, error: null });
      const obj = {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          id: id?.split("-")?.length > 2 ? id?.split("-")[2]?.trim() : id,
        },
        callback: (res) => {
          const retailersWithAll =
            res?.content?.length > 0 ? ["ALL", ...res.content] : ["ALL"];
          type === "distributor"
            ? setDistributorList({
                loading: false,
                data: { ...res, content: retailersWithAll },
                error: null,
              })
            : setRetailerList({
                loading: false,
                data: { ...res, content: retailersWithAll },
                error: null,
              });
          setSearchFields((prev) => ({
            ...prev,
            retailer: retailersWithAll?.[0],
          }));
        },
        errCallback: (err) => {
          type === "distributor"
            ? setDistributorList({ loading: false, data: null, error: err })
            : setRetailerList({ loading: false, data: null, error: err });
        },
      };
      dispatch(triggerDistRetailerFilter(obj));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!searchFields?.state) return;
    dispatch(
      triggerFetchFilterList(
        {
          "Content-Type": "application/json",
          state: searchFields?.state,
        },
        (data) => {
          let partnerRoleArray = data?.content
            ?.filter(
              (obj) =>
                obj.partnerRoleType === "752" ||
                obj.partnerRoleType === "754" ||
                obj.partnerRoleType === "782"
            )
            .map(
              (obj) =>
                getPartnerRoleName(obj.partnerRoleType) +
                " - " +
                obj.partnerName +
                " - " +
                obj.partnerId
            );
          setZdArdMardList(partnerRoleArray);
          setSearchFields((prev) => ({
            ...prev,
            partner: partnerRoleArray?.[0],
          }));
        }
      )
    );
  }, [searchFields?.state, dispatch]);

  useEffect(() => {
    if (["nhq", "region head"]?.includes(partnerName?.toLowerCase())) {
      setSearchFields((prev) => ({
        ...prev,
        region: selectedRegion ? selectedRegion : config?.region[0],
      }));
    }
    if (
      ["nhq", "region head", "state head"]?.includes(partnerName?.toLowerCase())
    ) {
      setSearchFields((prev) => ({
        ...prev,
        state: selectedState
          ? selectedState
          : config?.states?.[
              config?.regionMaps?.[
                selectedRegion
                  ? selectedRegion
                  : config?.region?.[0]?.toUpperCase()
              ]
            ]?.[0],
      }));
    }
    if (partnerName?.toLowerCase().includes("zonal")) {
      setSearchFields((prev) => ({
        ...prev,
        distributor: selectedDistributor ? selectedDistributor : "ALL",
      }));
    }
  }, []);

  useEffect(() => {
    if (
      ["jc", "jp", "area"].includes(partnerName?.toLowerCase()) &&
      selectedRole &&
      selectedRole.includes(" - ") &&
      selectedRole !== searchFields?.partner
    ) {
      setSearchFields((prev) => ({
        ...prev,
        partner: selectedRole,
      }));
    }
  }, []);

  useEffect(() => {
    if (searchFields?.partner && searchFields?.partner?.includes("ZD - ")) {
      const partnerId = searchFields.partner.split("-")[2]?.trim();
      const list = filterList?.data?.content?.filter(
        (item) => item?.parentId === partnerId
      );
      const formattedList = list?.map(
        (item) =>
          `${getPartnerRoleName(item?.partnerRoleType)} - ${
            item?.partnerName
          } - ${item?.partnerId}`
      );
      const finalList = ["ALL", ...(formattedList || [])];
      setRdsList(finalList);

      setSearchFields((prev) => ({
        ...prev,
        RDS: finalList[0],
      }));
    } else if (
      searchFields?.partner &&
      !searchFields?.partner.includes("ZD - ")
    ) {
      setRdsList(null);
      setSearchFields((prev) => ({
        ...prev,
        RDS: null,
      }));
    }
  }, [filterList?.data?.content, searchFields?.partner]);

  useEffect(() => {
    const showHeader = () => {
      return dispatch(triggerShowHeader(true));
    };
    showHeader();
  }, [dispatch]);

  useEffect(() => {
    dispatch(setClawbackDashboard());
    return () => {
      dispatch(unsetClawbackDashboard());
    };
  }, [dispatch]);

  useEffect(() => {
    if (orgId) {
      if (partnerName?.toLowerCase().includes("zonal")) {
        distRetailerFilter(orgId, "distributor");
      } else {
        !partnerName?.toLowerCase()?.includes("jc") &&
          distRetailerFilter(orgId);
      }
    }
  }, [distRetailerFilter, orgId]);

  useEffect(() => {
    if (
      searchFields?.partner &&
      ((!searchFields?.partner.includes("ZD - ") && searchFields?.RDS) ||
        searchFields?.RDS?.includes("RDS - ") ||
        (partnerName?.toLowerCase()?.includes("jc") &&
          searchFields?.RDS !== undefined))
    ) {
      distRetailerFilter(
        searchFields?.partner.includes("ZD - ")
          ? searchFields?.RDS
          : searchFields?.partner
      );
    }
  }, [
    distRetailerFilter,
    searchFields?.partner,
    searchFields?.RDS,
    partnerName,
  ]);

  useEffect(() => {
    if (searchFields?.distributor && searchFields?.distributor !== "ALL") {
      distRetailerFilter(searchFields?.distributor?.split("-")[1]?.trim());
    }
  }, [searchFields?.distributor, distRetailerFilter]);

  useEffect(() => {
    fetchTimestamp();
  }, [fetchTimestamp]);

  return (
    <ClawbackReportView
      searchFields={searchFields}
      setSearchFields={setSearchFields}
      onRunClick={onRunClick}
      partnerName={partnerName}
      orgId={orgId}
      agentId={agentId}
      name={name}
      zdArdMardList={zdArdMardList}
      filterList={filterList}
      filteredArray={filteredArray}
      retailerList={retailerList}
      rdsList={rdsList}
      urlList={urlList}
      clawbackDetails={clawbackDetails}
      clawbackSummary={clawbackSummary}
      showClawbackSummary={showClawbackSummary}
      loading={loading}
      downloadCSV={downloadCSV}
      timeStamp={timeStamp}
      showDownloadIcon={showDownloadIcon}
      csvLoading={csvLoading}
      jcValue={jcValue}
      setJcValue={setJcValue}
      distributorList={distributorList}
      toggleShowDetails={toggleShowDetails}
      showAlert={showAlert}
      showDateAlert={showDateAlert}
      showErrorAlert={showErrorAlert}
      hideAlertEd1={hideAlertEd1}
      dispatch={dispatch}
    />
  );
};

export default ClawbackReport;
